import { useContext, useEffect, useRef } from 'react';
import maxBy from 'lodash/maxBy';
import { isMCQ } from '../utility/progressHelpers';
import {
  getQuestionByReference,
  doSetHiddenAnswer,
  toggleAnswerOptionsCrossOut,
} from '../utils';
import { SELECTORS } from '../constants/selectors';
import useGetPlayerEl from '../hooks/useGetPlayerEl';
import useFirstLoad from '../hooks/useFirstLoad';
import { BluebookContext } from '../state/BluebookContext';

const CrossOutInit = (props) => {
  const { state: playerState } = useContext(BluebookContext);
  const playerEl = useGetPlayerEl();
  const toggleEnabledRef = useRef(false);
  const { crossOutReducer, learnosityApp } = props;
  const [crossOutState, dispatchers] = crossOutReducer;
  const { isLearnosityApiReady, questions } = playerState;
  const { isEnabled, toggleCount } = crossOutState;

  useFirstLoad(playerState, (questionApi) => {
    const metadata = questionApi?.response?.getMetadata();
    setDefaultToggleCrossOutCount(metadata);
    if (metadata !== undefined) {
      toggleEnabledRef.current = true;
    }
    toggleAnswerOptionsCrossOut(playerEl, metadata?.crossOutToggleStatus);
    dispatchers.doInit();
    if (metadata?.crossOutToggleStatus) {
      dispatchers.enableCrossOut();
    }
  });

  const setDefaultToggleCrossOutCount = (metadata) => {
    const crossOutCount = (metadata && metadata?.crossOutToggleCount) || 0;
    dispatchers.setDefaultToggleCrossOutCount(crossOutCount);
  };

  useEffect(() => {
    if (toggleEnabledRef.current) {
      return;
    }

    if (isLearnosityApiReady) {
      const getNewestCrossOutMetadata = (responses) => {
        const responsesValues = Object.values(responses);
        const response = maxBy(
          responsesValues,
          (o) => o?.metadata?.crossOutToggleCount,
        );

        return response?.metadata || {};
      };

      const responses = learnosityApp?.getResponses() || {};
      const metadata = getNewestCrossOutMetadata(responses);
      const isOn = metadata?.crossOutToggleStatus || false;
      setDefaultToggleCrossOutCount(metadata);
      dispatchers.doInit();
      if (isOn) {
        dispatchers.toggleCrossOut();
      }
      toggleAnswerOptionsCrossOut(playerEl, isOn);
    }
  }, [isLearnosityApiReady, toggleEnabledRef]);

  useEffect(() => {
    const saveMainToggleMetadata = (learnosityResponseApi, metadata) => {
      learnosityResponseApi?.setMetadata({
        ...(metadata || {}),
        crossOutToggleStatus: isEnabled,
        crossOutToggleCount: toggleCount,
      });
    };

    const handleNoMetadata = (currentItem, response, learnosityQuestionApi) => {
      const baseSelector = `.learnosity-item[data-reference="${currentItem.reference}"] `;
      const firstAnswerOptionSelector = `${baseSelector}${SELECTORS.OPTION_ANSWER} ${SELECTORS.OPTION_ANSWER_INPUT}`;

      const firstAnswerOptionEl = playerEl?.current?.querySelector(
        firstAnswerOptionSelector,
      );

      if (firstAnswerOptionEl && !learnosityQuestionApi.isAttempted()) {
        const deferredCleanup = doSetHiddenAnswer(firstAnswerOptionEl);
        // *Allows the click event to take place, allow learnosity to config the answered question
        Promise.resolve().then(() => {
          learnosityQuestionApi.resetResponse();
          deferredCleanup();
          saveMainToggleMetadata(response);
        });
      }
    };

    const onItemReady = () => {
      const currentItem = learnosityApp?.getCurrentItem();
      if (!currentItem) return;
      const responseId = currentItem.response_ids?.[0];
      if (!responseId) return;
      const question = getQuestionByReference(currentItem.reference, questions);
      if (!isMCQ(question)) return;

      const learnosityQuestionApi = learnosityApp.question(responseId);
      const { response } = learnosityQuestionApi;
      const metadata = response?.getMetadata();

      metadata
        ? saveMainToggleMetadata(response, metadata)
        : handleNoMetadata(currentItem, response, learnosityQuestionApi);
    };
    if (isLearnosityApiReady) {
      learnosityApp.on('item:load', onItemReady);
    }

    return () => {
      if (isLearnosityApiReady) {
        learnosityApp.off('item:load', onItemReady);
      }
    };
  }, [isLearnosityApiReady, isEnabled, toggleCount, playerEl]);

  return null;
};

export default CrossOutInit;
